import React, { useRef, useState } from 'react';
import { Box, InputBase, Tooltip } from '@mui/material';
import { launchColors } from '../launchTheme';

interface Props {
  value: string;
  label: string;
  editable: boolean;
  maxLength: number;
  strong?: boolean;
  display?: React.ReactNode;
  onSave: (value: string) => Promise<boolean>;
}

export const cellTextSx = {
  fontSize: 13,
  lineHeight: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const EditableCell: React.FC<Props> = ({ value, label, editable, maxLength, strong, display, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const done = useRef(false);

  const start = () => {
    if (!editable || saving) return;
    done.current = false;
    setDraft(value);
    setEditing(true);
  };

  const commit = async () => {
    if (done.current) return;
    done.current = true;
    setEditing(false);
    if (draft === value) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    done.current = true;
    setEditing(false);
  };

  if (editing) {
    return (
      <InputBase
        autoFocus
        fullWidth
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => { void commit(); }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            cancel();
          } else if (event.key === 'Tab') {
            void commit();
          }
        }}
        inputProps={{ maxLength, 'aria-label': label }}
        sx={{
          fontSize: 13,
          px: 0.75,
          mx: -0.75,
          bgcolor: launchColors.card,
          borderRadius: '4px',
          boxShadow: `0 0 0 2px ${launchColors.navy}`,
          '& input': { py: 0.25 },
        }}
      />
    );
  }

  const shown = saving ? draft : value;
  const text = (
    <Box
      role={editable ? 'button' : undefined}
      tabIndex={editable ? 0 : undefined}
      aria-label={editable ? `${label}: ${shown || 'tom'}` : undefined}
      onClick={start}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === 'F2') {
          event.preventDefault();
          start();
        }
      }}
      sx={{
        ...cellTextSx,
        minHeight: 20,
        px: 0.75,
        mx: -0.75,
        borderRadius: '4px',
        fontWeight: strong ? 600 : 400,
        color: launchColors.text,
        opacity: saving ? 0.6 : 1,
        cursor: editable ? 'text' : 'default',
        '&:hover': editable ? { bgcolor: launchColors.softNavy } : undefined,
        '&:focus-visible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 0 },
      }}
    >
      {saving || display === undefined ? shown : display}
    </Box>
  );
  return display === undefined && shown.length > 24 ? <Tooltip title={shown} enterDelay={500} placement="top-start">{text}</Tooltip> : text;
};

export default EditableCell;
